import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Search, Loader2, Play, Trash2, Eye, FileDown, Copy, Download, RefreshCw, Tag, X, Clock, Upload, Truck, ChevronsUpDown, Check } from "lucide-react";
import { toast } from "sonner";
import html2pdf from "html2pdf.js";
import { LOGO_BASE64 } from "@/lib/logo";
import { DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

const NEW_WSP_MRP_BLOCK = `
<div style="display:flex;gap:16px;margin-top:14px;">
  <div style="flex:1;border:1px solid #E7E5E4;border-radius:6px;padding:10px 14px;">
    <div style="font-size:9px;color:#78716c;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:8px;">New WSP</div>
    <div style="height:22px;border-bottom:1px solid #ccc;"></div>
  </div>
  <div style="flex:1;border:1px solid #E7E5E4;border-radius:6px;padding:10px 14px;">
    <div style="font-size:9px;color:#78716c;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;margin-bottom:8px;">New MRP</div>
    <div style="height:22px;border-bottom:1px solid #ccc;"></div>
  </div>
</div>`;

const MAX_VISIBLE = 50;

function SearchableField({ label, testId, options, value, onChange, placeholder, displayFn }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const display = displayFn || ((v) => v);
  const filtered = options.filter((o) => display(o).toLowerCase().includes(search.toLowerCase()));
  const shown = filtered.slice(0, MAX_VISIBLE);

  if (value) {
    return (
      <div>
        <label className="text-xs font-medium text-stone-600 mb-1 block">{label}</label>
        <div
          data-testid={testId}
          className="border border-stone-200 rounded-md px-3 py-2 h-10 flex items-center justify-between bg-white cursor-pointer text-sm"
          onClick={() => { onChange(""); setSearch(""); setOpen(true); }}
        >
          <span className="truncate">{display(value)}</span>
          <span className="text-stone-400 text-xs hover:text-red-500 ml-2 shrink-0">change</span>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <label className="text-xs font-medium text-stone-600 mb-1 block">{label}</label>
      <input
        data-testid={testId}
        className="w-full border border-stone-200 rounded-md px-3 py-2 h-10 text-sm focus:outline-none focus:ring-1 focus:ring-[#C2410C] bg-white"
        placeholder={placeholder}
        value={search}
        onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {shown.length === 0 ? (
            <div className="px-3 py-3 text-sm text-stone-400 text-center">
              {search ? "No matches" : "Loading..."}
            </div>
          ) : (
            <>
              {shown.map((o) => (
                <div
                  key={o}
                  className="px-3 py-2 text-sm cursor-pointer hover:bg-orange-50 hover:text-[#C2410C] border-b border-stone-50 last:border-0"
                  onClick={() => { onChange(o); setSearch(""); setOpen(false); }}
                >
                  {display(o)}
                </div>
              ))}
              {filtered.length > MAX_VISIBLE && (
                <div className="px-3 py-2 text-xs text-stone-400 text-center bg-stone-50">
                  Showing {MAX_VISIBLE} of {filtered.length.toLocaleString()} — type to narrow down
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function SavedDesignsPage() {
  const [designs, setDesigns] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [cat2Filter, setCat2Filter] = useState("all");
  const [catPopOpen, setCatPopOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyDesign, setHistoryDesign] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [bulkTagFile, setBulkTagFile] = useState(null);
  const [bulkTagBusy, setBulkTagBusy] = useState(false);
  const [bulkTagResult, setBulkTagResult] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [vendorDialogOpen, setVendorDialogOpen] = useState(false);
  const [vendorDialogDesign, setVendorDialogDesign] = useState(null);
  const [pickedVendorIds, setPickedVendorIds] = useState(new Set());
  const [vendorSearch, setVendorSearch] = useState("");
  const [vendorBusy, setVendorBusy] = useState(false);
  const navigate = useNavigate();

  const openHistory = async (d, e) => {
    e?.stopPropagation();
    setHistoryDesign(d);
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryRows([]);
    try {
      const res = await api.get(`/saved-designs/${d.id}/history`);
      setHistoryRows(res.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setHistoryLoading(false);
    }
  };

  const submitBulkTag = async () => {
    if (!bulkTagFile) { toast.error("Please select a CSV file"); return; }
    setBulkTagBusy(true);
    setBulkTagResult(null);
    try {
      const text = await bulkTagFile.text();
      // Parse CSV: header row + data. Accepts columns: design_name, design_id, barcode
      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
      if (lines.length < 2) { toast.error("CSV must have a header row and at least one data row"); setBulkTagBusy(false); return; }
      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const nameIdx = headers.findIndex((h) => h === "design_name" || h === "name");
      const idIdx = headers.findIndex((h) => h === "design_id" || h === "id");
      const barIdx = headers.findIndex((h) => h === "barcode");
      if (barIdx === -1 || (nameIdx === -1 && idIdx === -1)) {
        toast.error("CSV must include a 'barcode' column and either 'design_name' or 'design_id'");
        setBulkTagBusy(false);
        return;
      }
      const rows = lines.slice(1).map((line) => {
        const cells = line.split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
        return {
          design_name: nameIdx >= 0 ? cells[nameIdx] : null,
          design_id: idIdx >= 0 ? cells[idIdx] : null,
          barcode: cells[barIdx],
        };
      });
      const res = await api.post("/saved-designs/bulk-tag-barcodes", { rows });
      setBulkTagResult(res.data);
      toast.success(`Tagged ${res.data.tagged} / Skipped ${res.data.skipped}`);
      await fetchDesigns();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBulkTagBusy(false);
    }
  };

  const toggleSelect = (id, e) => {
    e?.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (currentFiltered) => {
    setSelectedIds((prev) => {
      const filteredIds = currentFiltered.map((d) => d.id);
      const allSelected = filteredIds.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        filteredIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...filteredIds]);
    });
  };

  const buildDesignHtml = (d) => {
    const rd = d.result_data || {};
    const fd = d.form_data || {};
    const mode = rd.calc_mode || fd.calc_mode || "regular";
    const landed = (rd.landed_cost || rd.final_cost_per_dhoti || 0).toFixed(2);
    const wspM = rd.wsp_price && rd.landed_cost ? getMargins(rd.landed_cost, rd.wsp_price) : null;
    const mrpM = rd.mrp_price && rd.wsp_price ? getMargins(rd.wsp_price, rd.mrp_price) : null;
    const dateStr = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

    let html = "";

    if (mode === "fob") {
      const productCost = parseFloat(fd.product_cost || rd.product_cost || 0);
      const discountPct = parseFloat(fd.discount_pct || rd.discount_pct || 0);
      const trimsCost = parseFloat(fd.trims_cost || rd.trims_cost || 0);
      const whPct = rd.warehouse_pct || 12;
      const discountAmt = productCost * (discountPct / 100);
      const afterDisc = productCost - discountAmt;
      const costDhoti = afterDisc + trimsCost;
      const whCost = costDhoti * (whPct / 100);
      const hasDisc = discountPct > 0;

      html = `
      <div style="font-family:'Work Sans','IBM Plex Sans',sans-serif;max-width:700px;padding:0;font-size:11px;color:#1C1917;">
        <div style="text-align:center;margin-bottom:10px;padding:8px 0;">
          <img src="${LOGO_BASE64}" style="height:44px;margin:0 auto;" />
          <div style="font-size:11px;color:#78716c;margin-top:6px;letter-spacing:2px;text-transform:uppercase;font-weight:600;">FOB Cost Sheet</div>
        </div>
        <div style="background:linear-gradient(135deg,#C2410C,#7C2D12);color:white;border-radius:10px;padding:16px 20px;margin-bottom:12px;">
          <div style="font-size:14px;font-weight:700;text-align:center;margin-bottom:8px;">${d.design_name}</div>
          <div style="display:flex;justify-content:space-around;text-align:center;">
            <div><div style="font-size:9px;opacity:0.6;">Landed Cost</div><div style="font-size:20px;font-weight:700;">${landed}</div></div>
            <div><div style="font-size:9px;opacity:0.6;">WSP</div><div style="font-size:20px;font-weight:700;">${rd.wsp_price || "—"}</div>${wspM ? `<div style="font-size:12px;opacity:0.7;margin-top:2px;">MU ${wspM.mu}% / MD ${wspM.md}%</div>` : ""}</div>
            <div><div style="font-size:9px;opacity:0.6;">MRP</div><div style="font-size:20px;font-weight:700;">${rd.mrp_price || "—"}</div>${mrpM ? `<div style="font-size:12px;opacity:0.7;margin-top:2px;">MU ${mrpM.mu}% / MD ${mrpM.md}%</div>` : ""}</div>
          </div>
        </div>
        <div style="background:#F7F6F2;border:1px solid #E7E5E4;border-radius:8px;padding:12px 16px;margin-bottom:12px;">
          <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:6px;">FOB Inputs</div>
          <div style="display:flex;gap:24px;flex-wrap:wrap;font-size:11px;">
            <div><span style="color:#78716c;">Product Cost / Dhoti:</span> <b>${productCost.toFixed(2)}</b></div>
            ${hasDisc ? `<div><span style="color:#78716c;">Discount:</span> <b>${discountPct}%</b></div>` : ""}
            <div><span style="color:#78716c;">Trims Cost / Dhoti:</span> <b>${trimsCost.toFixed(2)}</b></div>
            <div><span style="color:#78716c;">Border:</span> <b>${fd.border_type || rd.border_type || "—"}</b></div>
            ${(d.vendor_names || []).length ? `<div style="width:100%;border-top:1px dashed #E7E5E4;padding-top:6px;margin-top:2px;"><span style="color:#78716c;">Vendors:</span> <b>${(d.vendor_names || []).join(", ")}</b></div>` : ""}
          </div>
        </div>
        <div style="background:white;border:1px solid #E7E5E4;border-radius:8px;padding:12px 16px;">
          <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:6px;">Cost Breakdown</div>
          <table style="width:100%;border-collapse:collapse;font-size:11px;">
            <tr><td style="padding:6px 0;color:#57534E;">Product Cost</td><td style="text-align:right;font-family:monospace;">${productCost.toFixed(2)}</td></tr>
            ${hasDisc ? `<tr><td style="padding:6px 0;color:#57534E;">Discount (${discountPct}%)</td><td style="text-align:right;font-family:monospace;color:#B91C1C;">-${discountAmt.toFixed(2)}</td></tr>` : ""}
            ${hasDisc ? `<tr><td style="padding:6px 0;color:#57534E;">After Discount</td><td style="text-align:right;font-family:monospace;">${afterDisc.toFixed(2)}</td></tr>` : ""}
            <tr><td style="padding:6px 0;color:#57534E;">Trims Cost</td><td style="text-align:right;font-family:monospace;">${trimsCost.toFixed(2)}</td></tr>
            <tr><td colspan="2" style="border-bottom:1px solid #e7e5e4;padding:3px 0;"></td></tr>
            <tr><td style="padding:6px 0;font-weight:600;">Cost / Dhoti</td><td style="text-align:right;font-family:monospace;font-weight:600;">${costDhoti.toFixed(2)}</td></tr>
            <tr><td style="padding:6px 0;color:#57534E;">Warehouse (${whPct}%)</td><td style="text-align:right;font-family:monospace;">${whCost.toFixed(2)}</td></tr>
            <tr><td colspan="2" style="border-bottom:1px solid #e7e5e4;padding:3px 0;"></td></tr>
            <tr style="background:#C2410C;color:white;font-weight:700;"><td style="padding:8px;">Landed Cost</td><td style="padding:8px;text-align:right;font-family:monospace;">${landed}</td></tr>
            ${rd.wsp_price && wspM ? `<tr><td style="padding:6px 0;font-weight:600;">WSP</td><td style="text-align:right;font-family:monospace;font-weight:600;">${rd.wsp_price}</td></tr><tr><td style="padding:6px 0;color:#57534E;">WSP Markup / Markdown</td><td style="text-align:right;font-family:monospace;">${wspM.mu}% / ${wspM.md}%</td></tr>` : ""}
            ${rd.mrp_price && mrpM ? `<tr><td style="padding:6px 0;font-weight:600;">MRP</td><td style="text-align:right;font-family:monospace;font-weight:600;">${rd.mrp_price}</td></tr><tr><td style="padding:6px 0;color:#57534E;">MRP Markup / Markdown</td><td style="text-align:right;font-family:monospace;">${mrpM.mu}% / ${mrpM.md}%</td></tr>` : ""}
          </table>
        </div>
        ${NEW_WSP_MRP_BLOCK}
        <div style="text-align:center;font-size:9px;color:#a8a29e;margin-top:12px;">Generated on ${dateStr}</div>
      </div>`;
    } else if (mode === "shirt") {
      const fabricRate = parseFloat(fd.fabric_rate || rd.fabric_rate || 0);
      const consumption = parseFloat(fd.consumption || rd.consumption || 0);
      const cmt = parseFloat(fd.cmt || rd.cmt || 0);
      const fabricCost = (fabricRate * consumption).toFixed(2);
      const costShirt = rd.cost_per_shirt || (fabricRate * consumption + cmt);
      const whPct = rd.warehouse_pct || 12;
      const whCost = rd.warehouse_cost || (costShirt * (whPct / 100));

      html = `
      <div style="font-family:'Work Sans','IBM Plex Sans',sans-serif;max-width:700px;padding:0;font-size:11px;color:#1C1917;">
        <div style="text-align:center;margin-bottom:10px;padding:8px 0;">
          <img src="${LOGO_BASE64}" style="height:44px;margin:0 auto;" />
          <div style="font-size:11px;color:#78716c;margin-top:6px;letter-spacing:2px;text-transform:uppercase;font-weight:600;">Shirt Cost Sheet</div>
        </div>
        <div style="background:linear-gradient(135deg,#C2410C,#7C2D12);color:white;border-radius:10px;padding:16px 20px;margin-bottom:12px;">
          <div style="font-size:14px;font-weight:700;text-align:center;margin-bottom:8px;">${d.design_name}</div>
          <div style="display:flex;justify-content:space-around;text-align:center;">
            <div><div style="font-size:9px;opacity:0.6;">Landed Cost</div><div style="font-size:20px;font-weight:700;">${landed}</div></div>
            <div><div style="font-size:9px;opacity:0.6;">WSP</div><div style="font-size:20px;font-weight:700;">${rd.wsp_price || "—"}</div>${wspM ? `<div style="font-size:12px;opacity:0.7;margin-top:2px;">MU ${wspM.mu}% / MD ${wspM.md}%</div>` : ""}</div>
            <div><div style="font-size:9px;opacity:0.6;">MRP</div><div style="font-size:20px;font-weight:700;">${rd.mrp_price || "—"}</div>${mrpM ? `<div style="font-size:12px;opacity:0.7;margin-top:2px;">MU ${mrpM.mu}% / MD ${mrpM.md}%</div>` : ""}</div>
          </div>
        </div>
        <div style="background:#F7F6F2;border:1px solid #E7E5E4;border-radius:8px;padding:12px 16px;margin-bottom:12px;">
          <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:6px;">Specifications</div>
          <div style="display:flex;gap:24px;font-size:11px;flex-wrap:wrap;">
            <div><span style="color:#78716c;">Fabric Rate:</span> <b>${fabricRate}/mtr</b></div>
            <div><span style="color:#78716c;">Consumption:</span> <b>${consumption}m</b></div>
            <div><span style="color:#78716c;">CMT Charge:</span> <b>${cmt}</b></div>
            ${(d.vendor_names || []).length ? `<div style="width:100%;border-top:1px dashed #E7E5E4;padding-top:6px;margin-top:2px;"><span style="color:#78716c;">Vendors:</span> <b>${(d.vendor_names || []).join(", ")}</b></div>` : ""}
          </div>
        </div>
        <div style="background:white;border:1px solid #E7E5E4;border-radius:8px;padding:12px 16px;">
          <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:6px;">Cost Breakdown</div>
          <table style="width:100%;border-collapse:collapse;font-size:11px;">
            <tr><td style="padding:6px 0;color:#57534E;">Fabric (${fabricRate} × ${consumption}m)</td><td style="text-align:right;font-family:monospace;">${fabricCost}</td></tr>
            <tr><td style="padding:6px 0;color:#57534E;">CMT Charge</td><td style="text-align:right;font-family:monospace;">${cmt.toFixed(2)}</td></tr>
            <tr><td colspan="2" style="border-bottom:1px solid #e7e5e4;padding:3px 0;"></td></tr>
            <tr><td style="padding:6px 0;font-weight:600;">Cost / Shirt</td><td style="text-align:right;font-family:monospace;font-weight:600;">${costShirt.toFixed(2)}</td></tr>
            <tr><td style="padding:6px 0;color:#57534E;">Warehouse (${whPct}%)</td><td style="text-align:right;font-family:monospace;">${whCost.toFixed(2)}</td></tr>
            <tr><td colspan="2" style="border-bottom:1px solid #e7e5e4;padding:3px 0;"></td></tr>
            <tr style="background:#C2410C;color:white;font-weight:700;"><td style="padding:8px;">Landed Cost</td><td style="padding:8px;text-align:right;font-family:monospace;">${landed}</td></tr>
            ${rd.wsp_price && wspM ? `<tr><td style="padding:6px 0;font-weight:600;">WSP</td><td style="text-align:right;font-family:monospace;font-weight:600;">${rd.wsp_price}</td></tr><tr><td style="padding:6px 0;color:#57534E;">WSP Markup / Markdown</td><td style="text-align:right;font-family:monospace;">${wspM.mu}% / ${wspM.md}%</td></tr>` : ""}
            ${rd.mrp_price && mrpM ? `<tr><td style="padding:6px 0;font-weight:600;">MRP</td><td style="text-align:right;font-family:monospace;font-weight:600;">${rd.mrp_price}</td></tr><tr><td style="padding:6px 0;color:#57534E;">MRP Markup / Markdown</td><td style="text-align:right;font-family:monospace;">${mrpM.mu}% / ${mrpM.md}%</td></tr>` : ""}
          </table>
        </div>
        ${NEW_WSP_MRP_BLOCK}
        <div style="text-align:center;font-size:9px;color:#a8a29e;margin-top:12px;">Generated on ${dateStr}</div>
      </div>`;
    } else {
      const s = parseFloat(fd.size) || 1;
      html = `
    <div style="font-family:'Work Sans','IBM Plex Sans',sans-serif;max-width:700px;font-size:11px;color:#1C1917;">
      <div style="text-align:center;margin-bottom:10px;padding:8px 0;">
        <img src="${LOGO_BASE64}" style="height:44px;margin:0 auto;" />
        <div style="font-size:11px;color:#78716c;margin-top:6px;letter-spacing:2px;text-transform:uppercase;font-weight:600;">${mode === "tissue" ? "Tissue" : "Dhoti"} Costing Sheet</div>
      </div>
      <div style="background:linear-gradient(135deg,#C2410C,#7C2D12);color:white;border-radius:10px;padding:16px 20px;margin-bottom:12px;">
        <div style="font-size:14px;font-weight:700;text-align:center;margin-bottom:8px;">${d.design_name}</div>
        <div style="display:flex;justify-content:space-around;text-align:center;">
          <div><div style="font-size:9px;opacity:0.6;">Landed Cost</div><div style="font-size:20px;font-weight:700;">${landed}</div></div>
          <div><div style="font-size:9px;opacity:0.6;">WSP</div><div style="font-size:20px;font-weight:700;">${rd.wsp_price || "—"}</div>${wspM ? `<div style="font-size:12px;opacity:0.7;margin-top:2px;">MU ${wspM.mu}% / MD ${wspM.md}%</div>` : ""}</div>
          <div><div style="font-size:9px;opacity:0.6;">MRP</div><div style="font-size:20px;font-weight:700;">${rd.mrp_price || "—"}</div>${mrpM ? `<div style="font-size:12px;opacity:0.7;margin-top:2px;">MU ${mrpM.mu}% / MD ${mrpM.md}%</div>` : ""}</div>
        </div>
      </div>
      <div style="background:#F7F6F2;border:1px solid #E7E5E4;border-radius:8px;padding:12px 16px;margin-bottom:12px;">
        <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:8px;">Primary Specifications</div>
        <div style="display:flex;gap:24px;flex-wrap:wrap;">
          <div style="display:flex;gap:24px;"><div><span style="color:#78716c;">Fabric Width:</span> <b>${fd.fabric_width}</b></div><div><span style="color:#78716c;">Border Width:</span> <b>${fd.border_width}</b></div><div><span style="color:#78716c;">Loom Reed:</span> <b>${fd.reed}</b></div><div><span style="color:#78716c;">Loom Pick:</span> <b>${fd.pick}</b></div><div><span style="color:#78716c;">Fabric Reed:</span> <b>${parseFloat(fd.reed) + 4}</b></div><div><span style="color:#78716c;">Fabric Pick:</span> <b>${parseFloat(fd.pick) - 2}</b></div><div><span style="color:#78716c;">Pick Rate:</span> <b>${fd.pick_rate}</b></div><div><span style="color:#78716c;">Size:</span> <b>${fd.size}m</b></div></div>
          <div style="display:flex;gap:24px;"><div><span style="color:#78716c;">Border:</span> <b>${fd.border_type}</b></div><div><span style="color:#78716c;">Bobbin:</span> <b>${fd.bobbin_type}</b></div><div><span style="color:#78716c;">Processing:</span> <b>${rd.processing_type || fd.processing_type || "—"}</b></div></div>
          <div style="display:flex;gap:24px;"><div><span style="color:#78716c;">Warp:</span> <b>${rd.warp_count}/${rd.warp_quality}</b> <span style="color:#78716c;font-size:9px;">@ ${rd.warp_price_per_unit ?? "—"} + ${rd.warp_sizing_per_unit ?? "—"}</span></div><div><span style="color:#78716c;">Weft:</span> <b>${rd.weft_count}/${rd.weft_quality}</b> <span style="color:#78716c;font-size:9px;">@ ${rd.weft_price_per_unit ?? "—"}</span></div><div><span style="color:#78716c;">Kuri:</span> <b>${rd.kuri_count}/${rd.kuri_quality}</b> <span style="color:#78716c;font-size:9px;">@ ${rd.kuri_price_per_unit ?? "—"}</span></div></div>
          <div style="display:flex;gap:24px;"><div><span style="color:#78716c;">Reed Space:</span> <b>${rd.reed_space}</b></div><div><span style="color:#78716c;">Warp Ends:</span> <b>${rd.warp_ends}</b></div><div><span style="color:#78716c;">Warp Wt/m:</span> <b>${rd.warp_weight_per_mtr?.toFixed(4)}</b></div><div><span style="color:#78716c;">Weft Wt/m:</span> <b>${rd.weft_weight_per_mtr?.toFixed(4)}</b></div></div>
          ${(d.vendor_names || []).length ? `<div style="width:100%;border-top:1px dashed #E7E5E4;padding-top:6px;margin-top:2px;"><span style="color:#78716c;">Vendors:</span> <b>${(d.vendor_names || []).join(", ")}</b></div>` : ""}
        </div>
      </div>
      <div style="display:flex;gap:12px;">
        <div style="flex:1;background:white;border:1px solid #E7E5E4;border-radius:8px;padding:14px 16px;">
          <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:10px;">Cost Breakdown</div>
          <table style="width:100%;border-collapse:collapse;font-size:11px;">
            <tr style="border-bottom:1px solid #eee;font-size:9px;color:#78716c;font-weight:600;text-transform:uppercase;"><td></td><td style="text-align:right;padding:4px 0;">Per Mtr</td><td style="text-align:right;padding:4px 0;">Per Dhoti</td></tr>
            <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Warp Raw</td><td style="text-align:right;font-family:monospace;">${rd.warp_raw_cost?.toFixed(2)||"—"}</td><td style="text-align:right;font-family:monospace;">${rd.warp_raw_cost?(rd.warp_raw_cost*s).toFixed(2):"—"}</td></tr>
            <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Warp Sizing</td><td style="text-align:right;font-family:monospace;">${rd.warp_sizing_charge?.toFixed(2)||"—"}</td><td style="text-align:right;font-family:monospace;">${rd.warp_sizing_charge?(rd.warp_sizing_charge*s).toFixed(2):"—"}</td></tr>
            <tr style="border-bottom:1px solid #e7e5e4;font-weight:600;"><td style="padding:5px 0;">Warp Total</td><td style="text-align:right;font-family:monospace;">${rd.warp_total_cost?.toFixed(2)||"—"}</td><td style="text-align:right;font-family:monospace;">${rd.warp_total_cost?(rd.warp_total_cost*s).toFixed(2):"—"}</td></tr>
            <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Weft</td><td style="text-align:right;font-family:monospace;">${rd.weft_cost?.toFixed(2)||"—"}</td><td style="text-align:right;font-family:monospace;">${rd.weft_cost?(rd.weft_cost*s).toFixed(2):"—"}</td></tr>
            <tr style="border-bottom:1px solid #e7e5e4;font-weight:600;"><td style="padding:5px 0;">Total Bobbin</td><td style="text-align:right;font-family:monospace;">${rd.total_bobbin_cost?.toFixed(2)||"—"}</td><td style="text-align:right;font-family:monospace;">${rd.total_bobbin_cost?(rd.total_bobbin_cost*s).toFixed(2):"—"}</td></tr>
            <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Kuri</td><td style="text-align:right;font-family:monospace;">—</td><td style="text-align:right;font-family:monospace;">${rd.kuri_cost?.toFixed(2)||"—"}</td></tr>
            <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Weaving Wages</td><td style="text-align:right;font-family:monospace;">${rd.weaving_wages?.toFixed(2)||"—"}</td><td style="text-align:right;font-family:monospace;">${rd.weaving_wages?(rd.weaving_wages*s).toFixed(2):"—"}</td></tr>
            <tr style="border-bottom:2px solid #C2410C;font-weight:700;"><td style="padding:6px 0;">Cost</td><td style="text-align:right;font-family:monospace;">${rd.cost_per_mtr?.toFixed(2)||"—"}</td><td style="text-align:right;font-family:monospace;">${rd.cost_per_dhoti?.toFixed(2)||"—"}</td></tr>
            <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Weaver Profit (${rd.weaver_profit_pct||10}%)</td><td></td><td style="text-align:right;font-family:monospace;">${rd.profit_amount?.toFixed(2)||"—"}</td></tr>
            <tr style="font-weight:700;font-size:12px;color:#C2410C;"><td style="padding:8px 0;">Greige Cost</td><td></td><td style="text-align:right;font-family:monospace;">${rd.greige_cost_per_dhoti?.toFixed(2)||"—"}</td></tr>
          </table>
        </div>
        <div style="flex:1;display:flex;flex-direction:column;gap:12px;">
          <div style="background:white;border:1px solid #E7E5E4;border-radius:8px;padding:14px 16px;">
            <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:10px;">Post Production</div>
            <table style="width:100%;border-collapse:collapse;font-size:11px;">
              <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Processing <span style="color:#a8a29e;font-size:10px;">(${rd.processing_mtr_rate||0}/mtr x ${s})</span></td><td style="text-align:right;font-family:monospace;">${rd.processing_cost?.toFixed(2)||"—"}</td></tr>
              <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Folding</td><td style="text-align:right;font-family:monospace;">${rd.folding_cost?.toFixed(2)||"—"}</td></tr>
              <tr style="border-bottom:1px solid #e7e5e4;"><td style="padding:5px 0;color:#57534E;">Trims</td><td style="text-align:right;font-family:monospace;">${rd.trims_cost?.toFixed(2)||"—"}</td></tr>
              <tr style="border-bottom:2px solid #1C1917;font-weight:700;"><td style="padding:6px 0;">Final Cost</td><td style="text-align:right;font-family:monospace;">${rd.final_cost_per_dhoti?.toFixed(2)||"—"}</td></tr>
              <tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:5px 0;color:#57534E;">Warehouse (${rd.warehouse_pct||5}%)</td><td style="text-align:right;font-family:monospace;">${rd.warehouse_cost?.toFixed(2)||"—"}</td></tr>
              <tr style="font-weight:700;font-size:13px;color:#C2410C;"><td style="padding:8px 0;">Landed Cost</td><td style="text-align:right;font-family:monospace;">${landed}</td></tr>
            </table>
          </div>
          <div style="background:white;border:1px solid #E7E5E4;border-radius:8px;padding:14px 16px;">
            <div style="font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:#78716c;margin-bottom:10px;">Pricing</div>
            <table style="width:100%;border-collapse:collapse;font-size:11px;">
              ${rd.wsp_price && wspM ? `<tr style="border-bottom:1px solid #f5f5f4;font-weight:600;"><td style="padding:5px 0;">WSP</td><td style="text-align:right;font-family:monospace;font-size:13px;">${rd.wsp_price}</td></tr><tr style="border-bottom:1px solid #f5f5f4;"><td style="padding:4px 0;color:#78716c;font-size:10px;">MU / MD</td><td style="text-align:right;color:#78716c;font-size:10px;">${wspM.mu}% / ${wspM.md}%</td></tr>` : ""}
              ${rd.mrp_price && mrpM ? `<tr style="border-bottom:1px solid #f5f5f4;font-weight:600;"><td style="padding:5px 0;">MRP</td><td style="text-align:right;font-family:monospace;font-size:13px;">${rd.mrp_price}</td></tr><tr><td style="padding:4px 0;color:#78716c;font-size:10px;">MU / MD</td><td style="text-align:right;color:#78716c;font-size:10px;">${mrpM.mu}% / ${mrpM.md}%</td></tr>` : ""}
              ${!rd.wsp_price && !rd.mrp_price ? `<tr><td style="padding:5px 0;color:#a8a29e;" colspan="2">No pricing set</td></tr>` : ""}
            </table>
          </div>
        </div>
      </div>
      ${NEW_WSP_MRP_BLOCK}
      <div style="text-align:center;font-size:8px;color:#a8a29e;margin-top:10px;">Generated on ${dateStr}</div>
    </div>`;
    }

    return html;
  };

  // Shared iframe-based PDF pipeline — used by single & bulk exports.
  // Render into a hidden iframe so html2canvas's internal document clone is small
  // (otherwise it clones the entire Saved Designs page → very slow with 400+ rows).
  const runPdfFromHtml = (htmlContent, filename, opts = {}) => {
    const orientation = opts.orientation || "portrait";
    const width = opts.width || 800;
    const loadingToast = toast.loading("Generating PDF...");
    const iframe = document.createElement("iframe");
    iframe.style.cssText = `position:fixed;left:0;top:0;width:${width}px;height:1200px;border:0;opacity:0;pointer-events:none;z-index:-1;`;
    iframe.setAttribute("aria-hidden", "true");
    document.body.appendChild(iframe);
    const cleanup = () => { try { document.body.removeChild(iframe); } catch (_) { /* already removed */ } };

    iframe.onload = () => {
      const idoc = iframe.contentDocument;
      if (!idoc) { cleanup(); toast.dismiss(loadingToast); toast.error("Failed to export PDF"); return; }
      idoc.body.style.margin = "0";
      idoc.body.innerHTML = htmlContent;
      setTimeout(() => {
        html2pdf().set({
          margin: [8, 8, 8, 8],
          filename,
          image: { type: "jpeg", quality: 0.92 },
          html2canvas: { scale: 1.5, useCORS: true, logging: false, windowWidth: width },
          jsPDF: { unit: "mm", format: "a4", orientation, compress: true },
          pagebreak: { mode: ["css", "legacy"] },
        }).from(idoc.body).save().then(() => {
          cleanup(); toast.dismiss(loadingToast); toast.success("PDF exported!");
        }).catch(() => {
          cleanup(); toast.dismiss(loadingToast); toast.error("Failed to export PDF");
        });
      }, 50);
    };

    iframe.srcdoc = "<!DOCTYPE html><html><head><meta charset='utf-8'></head><body></body></html>";
  };

  const exportDesignPdf = (d, e) => {
    e.stopPropagation();
    // Trigger the calculator/combo page's own Export PDF button so output exactly
    // matches the costing-page export (single source of truth, no duplicate template).
    const mode = d?.result_data?.calc_mode || d?.form_data?.calc_mode || "regular";
    const target = mode === "combo" ? `/combo?load=${d.id}&autoExport=1` : `/calculator?load=${d.id}&autoExport=1`;
    navigate(target);
  };

  const exportSelectedPdf = () => {
    const list = filtered.filter((d) => selectedIds.has(d.id));
    if (list.length === 0) { toast.info("Select at least one design"); return; }
    const joined = list.map((d, i) =>
      (i === 0 ? "" : `<div style="page-break-before:always;"></div>`) + buildDesignHtml(d)
    ).join("");
    runPdfFromHtml(joined, `Costing_${list.length}_designs_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const fetchDesigns = useCallback(async () => {
    try {
      const res = await api.get("/saved-designs");
      setDesigns(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // Compute margins from stored prices (avoids stale saved margin values)
  const getMargins = (cost, price) => {
    if (!cost || !price || cost <= 0 || price <= 0) return { mu: "—", md: "—" };
    const mu = (((price - cost) / cost) * 100).toFixed(2);
    const md = (((price - cost) / price) * 100).toFixed(2);
    return { mu, md };
  };

  useEffect(() => {
    fetchDesigns();
    api.get("/vendors").then((res) => setVendors(res.data || [])).catch(() => {});
  }, [fetchDesigns]);

  const openVendorDialog = (d, e) => {
    e?.stopPropagation();
    setVendorDialogDesign(d);
    setPickedVendorIds(new Set(d.vendor_ids || []));
    setVendorSearch("");
    setVendorDialogOpen(true);
  };
  const togglePickedVendor = (id) => {
    setPickedVendorIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const saveVendorTags = async () => {
    if (!vendorDialogDesign) return;
    setVendorBusy(true);
    try {
      const res = await api.put(`/saved-designs/${vendorDialogDesign.id}/vendors`, {
        vendor_ids: Array.from(pickedVendorIds),
      });
      toast.success(`Tagged ${res.data.count} vendor${res.data.count === 1 ? "" : "s"}`);
      // Update local state
      setDesigns((prev) => prev.map((d) =>
        d.id === vendorDialogDesign.id ? { ...d, vendor_ids: Array.from(pickedVendorIds), vendor_names: res.data.vendor_names } : d
      ));
      setVendorDialogOpen(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setVendorBusy(false); }
  };

  const exportCsv = () => {
    if (filtered.length === 0) return;
    const headers = ["Design Name", "Type", "Description", "Barcode", "Discount %", "Cost", "Trims", "WH Exp", "Final Cost", "WSP", "WSP MU", "WSP MD", "MRP", "MRP MU", "MRP MD"];
    const rows = filtered.map((d) => {
      const rd = d.result_data || {};
      const fd = d.form_data || {};
      const landed = rd.landed_cost || rd.final_cost_per_dhoti || 0;
      const wspM = rd.wsp_price && landed ? getMargins(landed, rd.wsp_price) : { mu: "", md: "" };
      const mrpM = rd.mrp_price && rd.wsp_price ? getMargins(rd.wsp_price, rd.mrp_price) : { mu: "", md: "" };
      const mode = rd.calc_mode || fd.calc_mode || "regular";
      const trims = rd.trims_cost || parseFloat(fd.trims_cost) || 0;
      const whCost = rd.warehouse_cost || 0;
      // Cost before trims and warehouse
      const finalCostBeforeWh = rd.final_cost_per_dhoti || rd.cost_before_wh || (landed > 0 && rd.warehouse_pct > 0 ? landed / (1 + rd.warehouse_pct / 100) : landed);
      const costWithoutTrims = finalCostBeforeWh - trims;
      const discount = fd.discount_pct ?? rd.discount_pct ?? "";
      return [
        `"${(d.design_name || "").replace(/"/g, '""')}"`,
        mode.charAt(0).toUpperCase() + mode.slice(1),
        `"${(d.description || "").replace(/"/g, '""')}"`,
        `"${(d.tagged_barcode || "").replace(/"/g, '""')}"`,
        discount === "" || discount === null || discount === undefined ? "" : discount,
        costWithoutTrims ? costWithoutTrims.toFixed(2) : "",
        trims ? trims.toFixed(2) : "0.00",
        whCost ? whCost.toFixed(2) : "0.00",
        landed ? landed.toFixed(2) : "",
        rd.wsp_price || "",
        wspM.mu,
        wspM.md,
        rd.mrp_price || "",
        mrpM.mu,
        mrpM.md,
      ].join(",");
    });
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Saved_Designs_${typeFilter !== "all" ? typeFilter + "_" : ""}${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported!");
  };

  const [recalculating, setRecalculating] = useState(false);
  const [refreshingMeta, setRefreshingMeta] = useState(false);

  const refreshTaggedMetadata = async () => {
    setRefreshingMeta(true);
    try {
      const res = await api.post("/saved-designs/refresh-tagged-metadata");
      const { tagged_designs_count, designs_synced, designs_unchanged } = res.data;
      toast.success(`Refreshed ${tagged_designs_count} tagged designs — ${designs_synced} pricing changes saved to history`);
      fetchDesigns();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRefreshingMeta(false);
    }
  };

  const exportPricelistCsv = () => {
    const taggedOnly = filtered.filter((d) => d.tagged_barcode && d.tagged_item);
    if (taggedOnly.length === 0) { toast.error("No tagged designs in the current filter to export"); return; }
    const headers = [
      "Barcode", "Design Name", "Shade", "Size", "Description",
      "Landed Cost", "WSP", "MRP", "WSP MU vs Cost", "WSP MD vs Cost",
    ];
    const rows = taggedOnly.map((d) => {
      const ti = d.tagged_item || {};
      const rd = d.result_data || {};
      const landed = rd.landed_cost || rd.final_cost_per_dhoti || 0;
      const wsp = rd.wsp_price || ti.wsp || 0;
      const mrp = rd.mrp_price || ti.mrp || 0;
      const m = wsp && landed ? getMargins(landed, wsp) : { mu: "", md: "" };
      return [
        `"${(ti.barcode || d.tagged_barcode || "").replace(/"/g, '""')}"`,
        `"${(d.design_name || ti.design_name || "").replace(/"/g, '""')}"`,
        `"${(ti.shade || "").replace(/"/g, '""')}"`,
        `"${(ti.size || "").replace(/"/g, '""')}"`,
        `"${(ti.description || "").replace(/"/g, '""')}"`,
        landed ? landed.toFixed(2) : "",
        wsp || "",
        mrp || "",
        m.mu,
        m.md,
      ].join(",");
    });
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Pricelist_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Pricelist exported (${taggedOnly.length} rows)`);
  };

  // ─── Pricelist PDF — one page per Category2, with per-design multi-select ───
  const [pricelistPdfOpen, setPricelistPdfOpen] = useState(false);
  const [pickedDesignIds, setPickedDesignIds] = useState(new Set());
  const [pricelistSearch, setPricelistSearch] = useState("");
  const [expandedCats, setExpandedCats] = useState(new Set());
  const [pricelistBusy, setPricelistBusy] = useState(false);

  const openPricelistPdf = () => {
    const taggedFiltered = filtered.filter((d) => d.tagged_barcode && d.tagged_item?.category2);
    setPickedDesignIds(new Set(taggedFiltered.map((d) => d.id)));
    // Expand all categories by default
    const cats = new Set(taggedFiltered.map((d) => d.tagged_item.category2));
    setExpandedCats(cats);
    setPricelistSearch("");
    setPricelistPdfOpen(true);
  };

  const togglePickedDesign = (id) => {
    setPickedDesignIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleCatExpanded = (c) => {
    setExpandedCats((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c); else next.add(c);
      return next;
    });
  };
  const toggleCatPick = (c, designsInCat) => {
    const ids = designsInCat.map((d) => d.id);
    const allPicked = ids.every((id) => pickedDesignIds.has(id));
    setPickedDesignIds((prev) => {
      const next = new Set(prev);
      if (allPicked) {
        ids.forEach((id) => next.delete(id));
      } else {
        ids.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const generatePricelistPdf = () => {
    if (pickedDesignIds.size === 0) { toast.error("Pick at least one design"); return; }
    // Group picked tagged designs by category2
    const groups = {};
    filtered.forEach((d) => {
      if (!d.tagged_barcode || !pickedDesignIds.has(d.id)) return;
      const c = d.tagged_item?.category2;
      if (!c) return;
      (groups[c] = groups[c] || []).push(d);
    });
    const orderedCats = Object.keys(groups).sort();
    if (orderedCats.length === 0) { toast.error("No tagged designs in selection"); return; }

    setPricelistBusy(true);

    const dateStr = new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    // Brand orange row banding
    const ROW_LIGHT = "#FFEDD5"; // orange-100
    const ROW_DARK = "#FED7AA";  // orange-200

    const pages = orderedCats.map((cat, pageIdx) => {
      const rows = groups[cat]
        .slice()
        .sort((a, b) => {
          const am = a.result_data?.mrp_price || a.tagged_item?.mrp || 0;
          const bm = b.result_data?.mrp_price || b.tagged_item?.mrp || 0;
          return am - bm;
        })
        .map((d, i) => {
          const ti = d.tagged_item || {};
          const rd = d.result_data || {};
          const fd = d.form_data || {};
          const mode = rd.calc_mode || fd.calc_mode || "regular";
          const typeLabel = mode === "regular" ? "Greige" : mode === "fob" ? "FOB" : mode === "tissue" ? "Tissue" : mode === "shirt" ? "Shirt" : mode.charAt(0).toUpperCase() + mode.slice(1);
          let baseCost = null;
          if (mode === "regular" || mode === "tissue") baseCost = rd.greige_cost_per_dhoti;
          else if (mode === "fob") baseCost = rd.product_cost ?? parseFloat(fd.product_cost);
          else if (mode === "shirt") baseCost = rd.cost_per_shirt;
          else if (mode === "combo") baseCost = rd.sub_total;
          const landed = rd.landed_cost || rd.final_cost_per_dhoti || 0;
          const wsp = rd.wsp_price || ti.wsp || 0;
          const mrp = rd.mrp_price || ti.mrp || 0;
          const m = wsp && landed ? getMargins(landed, wsp) : { mu: "—", md: "—" };
          const muNum = parseFloat(m.mu);
          const muColor = isNaN(muNum) ? "#1C1917" : (muNum <= 40 ? "#B91C1C" : muNum > 50 ? "#15803D" : "#1C1917");
          const bg = i % 2 === 0 ? ROW_LIGHT : ROW_DARK;
          const vendorsStr = (d.vendor_names || []).join(", ");
          return `<tr style="background:${bg};">
            <td style="padding:8px 10px;font-family:monospace;font-size:10px;color:#7C2D12;">${ti.barcode || d.tagged_barcode}</td>
            <td style="padding:8px 10px;color:#1C1917;font-weight:500;">${ti.design_name || d.design_name || ""}</td>
            <td style="padding:8px 10px;color:#1C1917;">${ti.shade || ""}</td>
            <td style="padding:8px 10px;color:#1C1917;">${ti.size || ""}</td>
            <td style="padding:8px 10px;font-size:10px;color:#44403C;">${ti.description || ""}</td>
            <td style="padding:8px 10px;color:#1C1917;font-size:10px;">${typeLabel}</td>
            <td style="padding:8px 10px;text-align:right;font-family:monospace;color:#1C1917;">${baseCost != null && !isNaN(baseCost) ? Number(baseCost).toFixed(2) : "—"}</td>
            <td style="padding:8px 10px;text-align:right;font-family:monospace;font-weight:700;color:#7C2D12;">${landed ? landed.toFixed(2) : "—"}</td>
            <td style="padding:8px 10px;text-align:right;font-family:monospace;color:#1C1917;">${wsp || "—"}</td>
            <td style="padding:8px 10px;text-align:right;font-family:monospace;color:#1C1917;">${mrp || "—"}</td>
            <td style="padding:8px 10px;text-align:right;font-family:monospace;font-weight:600;color:${muColor};">${m.mu}${m.mu !== "—" ? "%" : ""}</td>
            <td style="padding:8px 10px;text-align:right;font-family:monospace;color:#57534E;">${m.md}${m.md !== "—" ? "%" : ""}</td>
            <td style="padding:8px 10px;color:#1C1917;font-size:10px;white-space:normal;word-break:break-word;max-width:160px;">${vendorsStr}</td>
          </tr>`;
        }).join("");

      return `
        <div style="${pageIdx > 0 ? "page-break-before:always;" : ""}font-family:'Work Sans','IBM Plex Sans',sans-serif;padding:0;font-size:11px;color:#1C1917;">
          <div style="text-align:center;margin-bottom:14px;padding:8px 0;">
            <img src="${LOGO_BASE64}" style="height:42px;margin:0 auto;" />
            <div style="font-size:10px;color:#78716c;margin-top:6px;letter-spacing:2px;text-transform:uppercase;font-weight:600;">Pricelist · ${dateStr}</div>
          </div>
          <div style="background:linear-gradient(135deg,#C2410C,#7C2D12);color:white;border-radius:8px;padding:12px 18px;margin-bottom:0;display:flex;justify-content:space-between;align-items:center;">
            <div style="font-size:16px;font-weight:700;">${cat}</div>
            <div style="font-size:11px;opacity:0.85;">${groups[cat].length} item${groups[cat].length === 1 ? "" : "s"}</div>
          </div>
          <table style="width:100%;border-collapse:collapse;font-size:11px;border:1px solid #C2410C;">
            <thead>
              <tr style="background:#7C2D12;color:white;">
                <th style="padding:9px 10px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700;">Barcode</th>
                <th style="padding:9px 10px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700;">Design</th>
                <th style="padding:9px 10px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700;">Shade</th>
                <th style="padding:9px 10px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700;">Size</th>
                <th style="padding:9px 10px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700;">Description</th>
                <th style="padding:9px 10px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700;">Type</th>
                <th style="padding:9px 10px;text-align:right;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700;">Base Cost</th>
                <th style="padding:9px 10px;text-align:right;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700;">Landed</th>
                <th style="padding:9px 10px;text-align:right;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700;">WSP</th>
                <th style="padding:9px 10px;text-align:right;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700;">MRP</th>
                <th style="padding:9px 10px;text-align:right;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700;">MU vs Cost</th>
                <th style="padding:9px 10px;text-align:right;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700;">MD vs Cost</th>
                <th style="padding:9px 10px;text-align:left;font-size:9px;text-transform:uppercase;letter-spacing:1px;font-weight:700;">Vendors</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>`;
    }).join("");

    runPdfFromHtml(pages, `Pricelist_${new Date().toISOString().slice(0, 10)}.pdf`, { orientation: "landscape", width: 1100 });
    setPricelistPdfOpen(false);
    setPricelistBusy(false);
  };

  const recalculateAll = async () => {
    setRecalculating(true);
    try {
      const res = await api.post("/saved-designs/recalculate-all");
      toast.success(`Recalculated ${res.data.updated} of ${res.data.total} designs`);
      if (res.data.errors?.length) toast.error(`Failed: ${res.data.errors.join(", ")}`);
      fetchDesigns();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRecalculating(false);
    }
  };

  const recalculateSingle = async (id, e) => {
    e.stopPropagation();
    try {
      await api.post(`/saved-designs/${id}/recalculate`);
      toast.success("Design recalculated");
      fetchDesigns();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // Barcode tagging
  const [tagDesign, setTagDesign] = useState(null);
  const [tagCat4, setTagCat4] = useState("");
  const [tagDesignName, setTagDesignName] = useState("");
  const [tagShade, setTagShade] = useState("");
  const [tagSize, setTagSize] = useState("");
  const [tagBarcode, setTagBarcode] = useState("");
  const [cascading, setCascading] = useState({});
  const [tagLoading, setTagLoading] = useState(false);

  const resetTag = () => {
    setTagCat4(""); setTagDesignName(""); setTagShade(""); setTagSize(""); setTagBarcode("");
    setCascading({});
  };

  const openTagDialog = (d, e) => {
    e.stopPropagation();
    setTagDesign(d);
    resetTag();
    fetchCascading({});
  };

  const fetchCascading = async (params) => {
    try {
      const query = new URLSearchParams();
      if (params.category4) query.set("category4", params.category4);
      if (params.design_name) query.set("design_name", params.design_name);
      if (params.shade) query.set("shade", params.shade);
      if (params.size) query.set("size", params.size);
      const res = await api.get(`/item-master/cascading?${query.toString()}`);
      const data = res.data;
      setCascading((prev) => ({ ...prev, ...data }));

      // Auto-fill if only 1 option
      if (data.category4_options?.length === 1 && !params.category4) {
        const val = data.category4_options[0];
        setTagCat4(val);
        fetchCascading({ category4: val });
      } else if (data.design_name_options?.length === 1 && !params.design_name) {
        const val = data.design_name_options[0];
        setTagDesignName(val);
        fetchCascading({ ...params, design_name: val });
      } else if (data.shade_options?.length === 1 && !params.shade) {
        const val = data.shade_options[0];
        setTagShade(val);
        fetchCascading({ ...params, shade: val });
      } else if (data.size_options?.length === 1 && !params.size) {
        const val = data.size_options[0];
        setTagSize(val);
        fetchCascading({ ...params, size: val });
      } else if (data.items?.length === 1) {
        setTagBarcode(data.items[0].barcode);
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onCat4Change = (v) => { setTagCat4(v); setTagDesignName(""); setTagShade(""); setTagSize(""); setTagBarcode(""); setCascading((p) => ({ category4_options: p.category4_options })); fetchCascading({ category4: v }); };
  const onDesignNameChange = (v) => { setTagDesignName(v); setTagShade(""); setTagSize(""); setTagBarcode(""); setCascading((p) => ({ category4_options: p.category4_options, design_name_options: p.design_name_options })); fetchCascading({ category4: tagCat4, design_name: v }); };
  const onShadeChange = (v) => { setTagShade(v); setTagSize(""); setTagBarcode(""); setCascading((p) => ({ ...p, size_options: undefined, items: undefined })); fetchCascading({ category4: tagCat4, design_name: tagDesignName, shade: v }); };
  const onSizeChange = (v) => { setTagSize(v); setTagBarcode(""); setCascading((p) => ({ ...p, items: undefined })); fetchCascading({ category4: tagCat4, design_name: tagDesignName, shade: tagShade, size: v }); };

  const confirmTag = async () => {
    if (!tagBarcode || !tagDesign) return;
    setTagLoading(true);
    try {
      await api.put(`/saved-designs/${tagDesign.id}/tag-barcode`, { barcode: tagBarcode });
      toast.success("Barcode tagged successfully");
      setTagDesign(null);
      fetchDesigns();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setTagLoading(false); }
  };

  const untagBarcode = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/saved-designs/${id}/untag-barcode`);
      toast.success("Barcode untagged");
      fetchDesigns();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const deleteDesign = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/saved-designs/${id}`);
      toast.success("Design deleted");
      fetchDesigns();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const duplicateDesign = async (d, e) => {
    e.stopPropagation();
    try {
      await api.post("/saved-designs", {
        design_name: `${d.design_name} (Copy)`,
        description: d.description || "",
        form_data: d.form_data,
        result_data: d.result_data,
      });
      toast.success("Design duplicated");
      fetchDesigns();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const loadDesign = (id, e) => {
    e.stopPropagation();
    const d = designs.find((x) => x.id === id);
    const mode = d?.result_data?.calc_mode || d?.form_data?.calc_mode || "regular";
    if (mode === "combo") {
      navigate(`/combo?load=${id}`);
    } else {
      navigate(`/calculator?load=${id}`);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const filtered = designs.filter((d) => {
    // Type filter
    if (typeFilter !== "all") {
      const mode = d.result_data?.calc_mode || d.form_data?.calc_mode || "regular";
      if (mode !== typeFilter) return false;
    }
    // Category2 filter
    if (cat2Filter !== "all") {
      const cat2 = d.tagged_item?.category2 || "";
      if (cat2Filter === "__untagged__") {
        if (d.tagged_barcode) return false;
      } else if (cat2 !== cat2Filter) return false;
    }
    // Search filter
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      d.design_name?.toLowerCase().includes(q) ||
      d.description?.toLowerCase().includes(q) ||
      d.form_data?.border_type?.toLowerCase().includes(q) ||
      d.form_data?.warp_count?.toLowerCase().includes(q) ||
      d.form_data?.warp_quality?.toLowerCase().includes(q) ||
      d.form_data?.weft_count?.toLowerCase().includes(q) ||
      d.form_data?.weft_quality?.toLowerCase().includes(q) ||
      d.created_by?.toLowerCase().includes(q)
    );
  });

  // All distinct category2 values from currently tagged designs
  const allCat2Values = Array.from(new Set(
    designs.map((d) => d.tagged_item?.category2).filter((c) => c && c.trim())
  )).sort();

  return (
    <div className="space-y-6" data-testid="saved-designs-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">
          Saved Designs
        </h1>
        <p className="text-sm text-stone-500 mt-1">
          Browse, search, and load saved dhoti designs into the calculator.
        </p>
      </div>

      {/* Search + Filter + Export */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input
            data-testid="designs-search-input"
            placeholder="Search by name, description, border, yarn..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-11"
        />
        </div>
        <select
          data-testid="type-filter-select"
          className="h-11 px-3 border border-stone-200 rounded-md bg-white text-sm text-stone-700 focus:outline-none focus:ring-1 focus:ring-[#C2410C]"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="all">All Types</option>
          <option value="regular">Regular</option>
          <option value="fob">FOB</option>
          <option value="shirt">Shirt</option>
          <option value="tissue">Tissue</option>
          <option value="combo">Combo</option>
        </select>
        <Popover open={catPopOpen} onOpenChange={setCatPopOpen}>
          <PopoverTrigger asChild>
            <button
              data-testid="cat2-filter-select"
              role="combobox"
              aria-expanded={catPopOpen}
              className="h-11 px-3 border border-stone-200 rounded-md bg-white text-sm text-stone-700 focus:outline-none focus:ring-1 focus:ring-[#C2410C] max-w-[260px] min-w-[200px] flex items-center justify-between gap-2"
              title="Filter by Category 2 from tagged barcode"
            >
              <span className="truncate">
                {cat2Filter === "all" ? "All Categories" : cat2Filter === "__untagged__" ? "Untagged" : cat2Filter}
              </span>
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[260px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search category..." data-testid="cat2-filter-search" />
              <CommandList>
                <CommandEmpty>No category found.</CommandEmpty>
                <CommandGroup>
                  {[
                    { value: "all", label: "All Categories" },
                    { value: "__untagged__", label: "Untagged" },
                    ...allCat2Values.map((c) => ({ value: c, label: c })),
                  ].map((opt) => (
                    <CommandItem
                      key={opt.value}
                      value={opt.label}
                      onSelect={() => { setCat2Filter(opt.value); setCatPopOpen(false); }}
                      data-testid={`cat2-option-${opt.value}`}
                    >
                      <Check className={`mr-2 h-4 w-4 ${cat2Filter === opt.value ? "opacity-100" : "opacity-0"}`} />
                      {opt.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        <Button
          data-testid="csv-export-button"
          className="bg-[#C2410C] hover:bg-[#9A3412] text-white h-11"
          onClick={exportCsv}
          disabled={filtered.length === 0}
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSV ({filtered.length})
        </Button>
        <Button
          data-testid="export-selected-pdf-button"
          variant="outline"
          className="h-11 border-[#C2410C] text-[#C2410C] hover:bg-orange-50 disabled:opacity-50"
          onClick={exportSelectedPdf}
          disabled={selectedIds.size === 0}
          title="Export checked rows as a single multi-page PDF"
        >
          <FileDown className="h-4 w-4 mr-2" />
          Export Selected PDF{selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}
        </Button>
        <Button
          data-testid="bulk-tag-button"
          variant="outline"
          className="h-11 border-stone-300 text-stone-700 hover:bg-stone-100"
          onClick={() => { setBulkTagOpen(true); setBulkTagResult(null); setBulkTagFile(null); }}
          title="Tag barcodes from a CSV (columns: design_name OR design_id, barcode)"
        >
          <Upload className="h-4 w-4 mr-2" />
          Bulk Tag (CSV)
        </Button>
        <Button
          data-testid="refresh-metadata-button"
          variant="outline"
          className="h-11 border-stone-300 text-stone-700 hover:bg-stone-100"
          onClick={refreshTaggedMetadata}
          disabled={refreshingMeta}
          title="Pull latest categories, WSP & MRP from Item Master into all tagged designs"
        >
          {refreshingMeta ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          Refresh Tagged
        </Button>
        <Button
          data-testid="pricelist-export-button"
          className="bg-stone-800 hover:bg-stone-900 text-white h-11"
          onClick={exportPricelistCsv}
          title="Export pricelist of all tagged designs in the current filter"
        >
          <Download className="h-4 w-4 mr-2" />
          Pricelist CSV
        </Button>
        <Button
          data-testid="pricelist-pdf-button"
          variant="outline"
          className="h-11 border-stone-800 text-stone-800 hover:bg-stone-100"
          onClick={openPricelistPdf}
          title="Export pricelist as PDF — one page per Category 2"
        >
          <FileDown className="h-4 w-4 mr-2" />
          Pricelist PDF
        </Button>
        <Button
          data-testid="recalculate-all-button"
          variant="outline"
          className="h-11 border-[#C2410C] text-[#C2410C] hover:bg-orange-50"
          onClick={recalculateAll}
          disabled={recalculating || designs.length === 0}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${recalculating ? "animate-spin" : ""}`} />
          {recalculating ? "Recalculating..." : "Recalculate All"}
        </Button>
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-lg shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-3 py-3 w-10">
                <input
                  data-testid="select-all-checkbox"
                  type="checkbox"
                  className="h-4 w-4 accent-[#C2410C] cursor-pointer"
                  checked={filtered.length > 0 && filtered.every((d) => selectedIds.has(d.id))}
                  onChange={() => toggleSelectAll(filtered)}
                  title="Select all filtered designs"
                />
              </TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3">Design Name</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3">Border</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 text-right">Final Cost</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 text-right">WSP</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 text-right hidden sm:table-cell">WSP MU/MD</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 text-right">MRP</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 text-right hidden sm:table-cell">MRP MU/MD</TableHead>
              <TableHead className="text-xs font-semibold uppercase tracking-wider text-stone-500 px-4 py-3 w-28">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-stone-400" />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-stone-400">
                  {designs.length === 0
                    ? "No saved designs yet. Use the calculator and click Save to create one."
                    : "No matching designs found."}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((d, idx) => (
                <TableRow
                  key={d.id}
                  className="animate-row hover:bg-stone-50/50 cursor-pointer"
                  style={{ animationDelay: `${idx * 30}ms` }}
                  data-testid={`design-row-${d.id}`}
                  onClick={() => setSelected(d)}
                >
                  <TableCell className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      data-testid={`row-checkbox-${d.id}`}
                      type="checkbox"
                      className="h-4 w-4 accent-[#C2410C] cursor-pointer"
                      checked={selectedIds.has(d.id)}
                      onChange={(e) => toggleSelect(d.id, e)}
                    />
                  </TableCell>
                  <TableCell className="px-4 py-3 text-sm font-medium text-stone-900">
                    {d.design_name}
                    {d.result_data?.calc_mode === "fob" && (
                      <span className="ml-2 inline-flex text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded font-semibold">FOB</span>
                    )}
                    {d.result_data?.calc_mode === "shirt" && (
                      <span className="ml-2 inline-flex text-[10px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded font-semibold">Shirt</span>
                    )}
                    {d.result_data?.calc_mode === "tissue" && (
                      <span className="ml-2 inline-flex text-[10px] bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded font-semibold">Tissue</span>
                    )}
                    {d.result_data?.calc_mode === "combo" && (
                      <span className="ml-2 inline-flex text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-semibold">Combo</span>
                    )}
                    {d.tagged_barcode && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] bg-orange-50 text-[#C2410C] px-1.5 py-0.5 rounded-full font-normal">
                        {d.tagged_barcode}
                        <X className="h-2.5 w-2.5 cursor-pointer hover:text-red-600" onClick={(e) => untagBarcode(d.id, e)} />
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-sm text-stone-700">{d.form_data?.border_type || "-"}</TableCell>
                  <TableCell className="px-4 py-3 text-sm text-right font-mono font-semibold text-[#C2410C]">
                    {(d.result_data?.landed_cost || d.result_data?.final_cost_per_dhoti)?.toFixed(2) || "-"}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-sm text-right font-mono font-medium">{d.result_data?.wsp_price || "-"}</TableCell>
                  <TableCell className="px-4 py-3 text-sm text-right text-stone-500 hidden sm:table-cell">
                    {d.result_data?.wsp_price ? (() => { const m = getMargins(d.result_data?.landed_cost || d.result_data?.final_cost_per_dhoti, d.result_data.wsp_price); return `${m.mu}/${m.md}`; })() : "-"}
                  </TableCell>
                  <TableCell className="px-4 py-3 text-sm text-right font-mono font-medium">{d.result_data?.mrp_price || "-"}</TableCell>
                  <TableCell className="px-4 py-3 text-sm text-right text-stone-500 hidden sm:table-cell">
                    {d.result_data?.mrp_price && d.result_data?.wsp_price ? (() => { const m = getMargins(d.result_data.wsp_price, d.result_data.mrp_price); return `${m.mu}/${m.md}`; })() : "-"}
                  </TableCell>
                  <TableCell className="px-4 py-3">
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        data-testid={`load-design-${d.id}`}
                        className="h-7 bg-[#C2410C] hover:bg-[#9A3412] text-white text-xs px-3"
                        onClick={(e) => loadDesign(d.id, e)}
                      >
                        <Play className="h-3 w-3 mr-1" />
                        Load
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`pdf-design-${d.id}`}
                        className="h-7 w-7 text-stone-500 hover:text-[#C2410C] hover:bg-orange-50"
                        onClick={(e) => exportDesignPdf(d, e)}
                      >
                        <FileDown className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`recalculate-design-${d.id}`}
                        className="h-7 w-7 text-stone-500 hover:text-green-600 hover:bg-green-50"
                        onClick={(e) => recalculateSingle(d.id, e)}
                        title="Recalculate"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`history-design-${d.id}`}
                        className="h-7 w-7 text-stone-500 hover:text-blue-600 hover:bg-blue-50"
                        onClick={(e) => openHistory(d, e)}
                        title="View rate change history"
                      >
                        <Clock className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`vendor-tag-${d.id}`}
                        className={`h-7 w-7 ${d.vendor_ids?.length ? "text-[#C2410C]" : "text-stone-500"} hover:text-[#C2410C] hover:bg-orange-50 relative`}
                        onClick={(e) => openVendorDialog(d, e)}
                        title={d.vendor_names?.length ? `Tagged vendors: ${d.vendor_names.join(", ")}` : "Tag vendors"}
                      >
                        <Truck className="h-3.5 w-3.5" />
                        {d.vendor_ids?.length > 0 && (
                          <span className="absolute -top-1 -right-1 bg-[#C2410C] text-white text-[9px] rounded-full w-3.5 h-3.5 flex items-center justify-center font-semibold">
                            {d.vendor_ids.length}
                          </span>
                        )}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`tag-design-${d.id}`}
                        className={`h-7 w-7 ${d.tagged_barcode ? "text-[#C2410C]" : "text-stone-500"} hover:text-[#C2410C] hover:bg-orange-50`}
                        onClick={(e) => openTagDialog(d, e)}
                        title={d.tagged_barcode ? `Tagged: ${d.tagged_barcode}` : "Tag Barcode"}
                      >
                        <Tag className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`duplicate-design-${d.id}`}
                        className="h-7 w-7 text-stone-500 hover:text-blue-600 hover:bg-blue-50"
                        onClick={(e) => duplicateDesign(d, e)}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`delete-design-${d.id}`}
                        className="h-7 w-7 text-stone-400 hover:text-red-600 hover:bg-red-50"
                        onClick={(e) => deleteDesign(d.id, e)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {designs.length > 0 && (
        <p className="text-xs text-stone-400">
          Showing {filtered.length} of {designs.length} design(s)
        </p>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">{selected?.design_name}</DialogTitle>
            <DialogDescription>{selected?.description || "No description"}</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Specifications</p>
                <div className="grid grid-cols-2 gap-1.5 text-sm">
                  <span className="text-stone-500">Fabric Width</span>
                  <span className="text-right font-mono">{selected.form_data?.fabric_width}</span>
                  <span className="text-stone-500">Border Width</span>
                  <span className="text-right font-mono">{selected.form_data?.border_width}</span>
                  <span className="text-stone-500">Loom Reed</span>
                  <span className="text-right font-mono">{selected.form_data?.reed}</span>
                  <span className="text-stone-500">Loom Pick</span>
                  <span className="text-right font-mono">{selected.form_data?.pick}</span>
                  <span className="text-stone-500">Fabric Reed</span>
                  <span className="text-right font-mono">{parseFloat(selected.form_data?.reed) + 4}</span>
                  <span className="text-stone-500">Fabric Pick</span>
                  <span className="text-right font-mono">{parseFloat(selected.form_data?.pick) - 2}</span>
                  <span className="text-stone-500">Pick Rate</span>
                  <span className="text-right font-mono">{selected.form_data?.pick_rate}</span>
                  <span className="text-stone-500">Size</span>
                  <span className="text-right font-mono">{selected.form_data?.size} mtrs</span>
                  <span className="text-stone-500">Border Type</span>
                  <span className="text-right">{selected.form_data?.border_type}</span>
                  <span className="text-stone-500">Bobbin Type</span>
                  <span className="text-right capitalize">{selected.form_data?.bobbin_type}</span>
                </div>
              </div>
              <Separator />
              <div className="space-y-2">
                <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Yarn Selection</p>
                <div className="grid grid-cols-2 gap-1.5 text-sm">
                  <span className="text-stone-500">Warp</span>
                  <span className="text-right">{selected.form_data?.warp_count} / {selected.form_data?.warp_quality}</span>
                  <span className="text-stone-500">Weft</span>
                  <span className="text-right">{selected.form_data?.weft_count} / {selected.form_data?.weft_quality}</span>
                  <span className="text-stone-500">Kuri</span>
                  <span className="text-right">{selected.form_data?.kuri_count} / {selected.form_data?.kuri_quality}</span>
                </div>
              </div>
              {selected.result_data && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider">Pricing</p>
                    <div className="grid grid-cols-2 gap-1.5 text-sm">
                      <span className="text-stone-500">Landed Cost</span>
                      <span className="text-right font-mono font-semibold text-[#C2410C]">{(selected.result_data.landed_cost || selected.result_data.final_cost_per_dhoti)?.toFixed(2)}</span>
                      {selected.result_data.wsp_price && (() => {
                        const wm = getMargins(selected.result_data.landed_cost || selected.result_data.final_cost_per_dhoti, selected.result_data.wsp_price);
                        return (<>
                          <span className="text-stone-500">WSP</span>
                          <span className="text-right font-mono font-medium">{selected.result_data.wsp_price}</span>
                          <span className="text-stone-500">WSP Markup / Markdown</span>
                          <span className="text-right text-stone-600">{wm.mu}% / {wm.md}%</span>
                        </>);
                      })()}
                      {selected.result_data.mrp_price && selected.result_data.wsp_price && (() => {
                        const mm = getMargins(selected.result_data.wsp_price, selected.result_data.mrp_price);
                        return (<>
                          <span className="text-stone-500">MRP</span>
                          <span className="text-right font-mono font-medium">{selected.result_data.mrp_price}</span>
                          <span className="text-stone-500">MRP Markup / Markdown</span>
                          <span className="text-right text-stone-600">{mm.mu}% / {mm.md}%</span>
                        </>);
                      })()}
                    </div>
                  </div>
                </>
              )}
              <Separator />
              <Button
                data-testid="dialog-load-design"
                className="w-full bg-[#C2410C] hover:bg-[#9A3412] text-white"
                onClick={() => {
                  setSelected(null);
                  navigate(`/calculator?load=${selected.id}`);
                }}
              >
                <Play className="h-4 w-4 mr-2" />
                Load into Calculator
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Tag Barcode Dialog */}
      <Dialog open={!!tagDesign} onOpenChange={() => setTagDesign(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Tag Barcode</DialogTitle>
            <DialogDescription>Link an item master barcode to "{tagDesign?.design_name}"</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <SearchableField
              label="Category 4"
              testId="tag-cat4"
              options={cascading.category4_options || []}
              value={tagCat4}
              onChange={onCat4Change}
              placeholder="Search Category 4..."
            />
            {tagCat4 && (
              <SearchableField
                label="Design Name"
                testId="tag-design-name"
                options={cascading.design_name_options || []}
                value={tagDesignName}
                onChange={onDesignNameChange}
                placeholder="Type to search design name..."
              />
            )}
            {tagDesignName && (
              <SearchableField
                label="Shade"
                testId="tag-shade"
                options={cascading.shade_options || []}
                value={tagShade}
                onChange={onShadeChange}
                placeholder="Search shade..."
              />
            )}
            {tagShade && (
              <SearchableField
                label="Size"
                testId="tag-size"
                options={cascading.size_options || []}
                value={tagSize}
                onChange={onSizeChange}
                placeholder="Search size..."
              />
            )}
            {tagSize && cascading.items && (
              <SearchableField
                label="Description / Barcode"
                testId="tag-description"
                options={(cascading.items || []).map((item) => item.barcode)}
                displayFn={(bc) => {
                  const item = (cascading.items || []).find((i) => i.barcode === bc);
                  return item ? `${item.description || bc} — WSP: ${item.wsp} / MRP: ${item.mrp}` : bc;
                }}
                value={tagBarcode}
                onChange={setTagBarcode}
                placeholder="Search barcode or description..."
              />
            )}
            {tagBarcode && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-green-800">Selected: {tagBarcode}</p>
                <p className="text-xs text-green-600 mt-0.5">WSP/MRP will auto-fill if not already set on this design.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagDesign(null)}>Cancel</Button>
            <Button
              data-testid="tag-confirm-button"
              className="bg-[#C2410C] hover:bg-[#9A3412] text-white"
              disabled={!tagBarcode || tagLoading}
              onClick={confirmTag}
            >
              {tagLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Tag className="h-4 w-4 mr-2" />}
              Tag Barcode
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="history-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg">Rate Change History</DialogTitle>
            <p className="text-sm text-stone-500 mt-1">
              {historyDesign?.design_name} — snapshots of past values before each update or recalculation.
            </p>
          </DialogHeader>
          {historyLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>
          ) : historyRows.length === 0 ? (
            <div className="text-center py-12 text-stone-400 text-sm">No history yet. Updates and recalculations will start appearing here.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead className="text-xs uppercase tracking-wider">When</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Change</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Landed</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">WSP</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">MRP</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right">Greige</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right hidden md:table-cell">Warp Total</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-right hidden md:table-cell">Weft</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider hidden lg:table-cell">By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {historyRows.map((h, i) => {
                    const rd = h.result_data || {};
                    const landed = rd.landed_cost ?? rd.final_cost_per_dhoti;
                    return (
                      <TableRow key={i} data-testid={`history-row-${i}`}>
                        <TableCell className="text-sm font-mono">{new Date(h.changed_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</TableCell>
                        <TableCell><span className={`text-xs px-2 py-0.5 rounded font-medium ${h.change_type === "recalculate_all" ? "bg-amber-50 text-amber-700" : h.change_type === "recalculate" ? "bg-green-50 text-green-700" : "bg-blue-50 text-blue-700"}`}>{h.change_type.replace(/_/g, " ")}</span></TableCell>
                        <TableCell className="text-right font-mono text-sm font-semibold text-[#C2410C]">{landed?.toFixed?.(2) ?? "—"}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{rd.wsp_price ?? "—"}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{rd.mrp_price ?? "—"}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{rd.greige_cost_per_dhoti?.toFixed?.(2) ?? "—"}</TableCell>
                        <TableCell className="text-right font-mono text-sm hidden md:table-cell">{rd.warp_total_cost?.toFixed?.(2) ?? "—"}</TableCell>
                        <TableCell className="text-right font-mono text-sm hidden md:table-cell">{rd.weft_cost?.toFixed?.(2) ?? "—"}</TableCell>
                        <TableCell className="text-xs text-stone-500 hidden lg:table-cell">{h.changed_by || "—"}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Tag Barcode CSV Dialog */}
      <Dialog open={bulkTagOpen} onOpenChange={setBulkTagOpen}>
        <DialogContent className="max-w-2xl" data-testid="bulk-tag-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Tag Barcodes from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-xs text-stone-600">
              <div className="font-semibold mb-1">CSV Format</div>
              <ul className="list-disc ml-5 space-y-0.5">
                <li>Header row required.</li>
                <li>Required column: <code className="font-mono bg-white px-1 rounded">barcode</code></li>
                <li>Required: one of <code className="font-mono bg-white px-1 rounded">design_name</code> or <code className="font-mono bg-white px-1 rounded">design_id</code></li>
                <li>Example: <code className="font-mono">design_name,barcode</code> then <code className="font-mono">Glow 1/2 Silver,GLOW001</code></li>
                <li>Barcode must exist in Item Master. WSP/MRP from the item auto-fills when those are blank on the design.</li>
              </ul>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5 block">CSV File</Label>
              <Input data-testid="bulk-tag-file-input" type="file" accept=".csv,text/csv" onChange={(e) => { setBulkTagFile(e.target.files?.[0] || null); setBulkTagResult(null); }} />
              {bulkTagFile && <div className="text-xs text-stone-500 mt-1">Selected: {bulkTagFile.name}</div>}
            </div>
            {bulkTagResult && (
              <div className="rounded-lg border p-3 text-sm space-y-2" data-testid="bulk-tag-result">
                <div className="flex gap-4 flex-wrap">
                  <span className="text-green-700 font-semibold">Tagged: {bulkTagResult.tagged}</span>
                  <span className="text-amber-700">Skipped: {bulkTagResult.skipped}</span>
                  {bulkTagResult.not_found_designs > 0 && <span className="text-stone-600">Missing designs: {bulkTagResult.not_found_designs}</span>}
                  {bulkTagResult.not_found_barcodes > 0 && <span className="text-stone-600">Missing barcodes: {bulkTagResult.not_found_barcodes}</span>}
                  {bulkTagResult.multiple_matches > 0 && <span className="text-stone-600">Duplicate names: {bulkTagResult.multiple_matches}</span>}
                </div>
                {bulkTagResult.errors?.length > 0 && (
                  <div className="max-h-48 overflow-y-auto text-xs border-t pt-2 mt-2 space-y-1">
                    {bulkTagResult.errors.slice(0, 50).map((er, i) => (
                      <div key={i} className="text-stone-600">
                        <span className="font-mono text-stone-400">Row {er.row}</span> — <span className="text-red-600">{er.reason}</span> <span className="text-stone-500">({er.input.design_name || er.input.design_id} → {er.input.barcode})</span>
                      </div>
                    ))}
                    {bulkTagResult.errors.length > 50 && <div className="text-stone-400 italic">…and {bulkTagResult.errors.length - 50} more</div>}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkTagOpen(false)}>Close</Button>
            <Button
              data-testid="bulk-tag-submit"
              className="bg-[#C2410C] hover:bg-[#9A3412] text-white"
              disabled={!bulkTagFile || bulkTagBusy}
              onClick={submitBulkTag}
            >
              {bulkTagBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Upload & Tag
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pricelist PDF — search + expandable category tree with per-design multi-select */}
      <Dialog open={pricelistPdfOpen} onOpenChange={setPricelistPdfOpen}>
        <DialogContent className="max-w-2xl" data-testid="pricelist-pdf-dialog">
          <DialogHeader>
            <DialogTitle>Pricelist PDF — Pick Designs</DialogTitle>
            <p className="text-xs text-stone-500 mt-1">Each Category 2 becomes its own page. Designs sorted by MRP (low → high) within each page.</p>
          </DialogHeader>

          {(() => {
            const tagged = filtered.filter((d) => d.tagged_barcode && d.tagged_item?.category2);
            if (tagged.length === 0) {
              return <div className="text-sm text-stone-400 text-center py-8">No tagged designs in the current filter.</div>;
            }
            // Apply search across both category2 and design_name
            const q = pricelistSearch.trim().toLowerCase();
            const matches = (d) => !q ||
              d.design_name?.toLowerCase().includes(q) ||
              d.tagged_item?.category2?.toLowerCase().includes(q) ||
              d.tagged_item?.shade?.toLowerCase().includes(q) ||
              d.tagged_barcode?.toLowerCase().includes(q);
            const visible = tagged.filter(matches);
            // Group by category2
            const grouped = {};
            visible.forEach((d) => {
              const c = d.tagged_item.category2;
              (grouped[c] = grouped[c] || []).push(d);
            });
            const cats = Object.keys(grouped).sort();
            const allVisibleIds = visible.map((d) => d.id);

            return (
              <>
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                    <Input
                      data-testid="pricelist-search-input"
                      placeholder="Search by design name, category, shade, barcode..."
                      value={pricelistSearch}
                      onChange={(e) => setPricelistSearch(e.target.value)}
                      className="pl-9 h-10"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setPickedDesignIds(new Set([...pickedDesignIds, ...allVisibleIds]))}>Select Visible</Button>
                    <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => {
                      const next = new Set(pickedDesignIds);
                      allVisibleIds.forEach((id) => next.delete(id));
                      setPickedDesignIds(next);
                    }}>Deselect Visible</Button>
                    <span className="ml-auto text-xs text-stone-500 self-center">
                      {pickedDesignIds.size} of {tagged.length} picked
                    </span>
                  </div>

                  <div className="max-h-[50vh] overflow-y-auto border border-stone-200 rounded-md divide-y divide-stone-100">
                    {cats.length === 0 ? (
                      <div className="text-sm text-stone-400 text-center py-6">No matches.</div>
                    ) : cats.map((c) => {
                      const designsInCat = grouped[c];
                      const ids = designsInCat.map((d) => d.id);
                      const pickedCount = ids.filter((id) => pickedDesignIds.has(id)).length;
                      const allPicked = pickedCount === ids.length;
                      const somePicked = pickedCount > 0 && !allPicked;
                      const isExpanded = expandedCats.has(c);
                      return (
                        <div key={c}>
                          <div className="flex items-center gap-2 px-3 py-2 bg-[#FFEDD5]/40 hover:bg-[#FFEDD5] cursor-pointer" onClick={() => toggleCatExpanded(c)}>
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-[#C2410C] cursor-pointer"
                              checked={allPicked}
                              ref={(el) => { if (el) el.indeterminate = somePicked; }}
                              onChange={(e) => { e.stopPropagation(); toggleCatPick(c, designsInCat); }}
                              onClick={(e) => e.stopPropagation()}
                            />
                            <span className="text-sm font-semibold text-[#7C2D12] flex-1">{c}</span>
                            <span className="text-xs text-stone-500">{pickedCount}/{ids.length}</span>
                            <span className="text-stone-400 text-xs">{isExpanded ? "▾" : "▸"}</span>
                          </div>
                          {isExpanded && (
                            <div className="pl-9 pr-3 py-1 bg-white">
                              {designsInCat
                                .slice()
                                .sort((a, b) => {
                                  const am = a.result_data?.mrp_price || a.tagged_item?.mrp || 0;
                                  const bm = b.result_data?.mrp_price || b.tagged_item?.mrp || 0;
                                  return am - bm;
                                })
                                .map((d) => (
                                  <label key={d.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-stone-50 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      className="h-3.5 w-3.5 accent-[#C2410C] cursor-pointer"
                                      checked={pickedDesignIds.has(d.id)}
                                      onChange={() => togglePickedDesign(d.id)}
                                    />
                                    <span className="text-sm flex-1 truncate" title={d.design_name}>{d.design_name}</span>
                                    <span className="text-xs text-stone-400 font-mono">{d.tagged_barcode}</span>
                                    <span className="text-xs text-stone-500 font-mono w-12 text-right">MRP {d.result_data?.mrp_price || d.tagged_item?.mrp || "—"}</span>
                                  </label>
                                ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            );
          })()}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPricelistPdfOpen(false)}>Cancel</Button>
            <Button
              data-testid="pricelist-pdf-generate"
              className="bg-[#C2410C] hover:bg-[#9A3412] text-white"
              disabled={pickedDesignIds.size === 0 || pricelistBusy}
              onClick={generatePricelistPdf}
            >
              {pricelistBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
              Generate PDF ({pickedDesignIds.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Tag Dialog — multi-select vendors per design */}
      <Dialog open={vendorDialogOpen} onOpenChange={setVendorDialogOpen}>
        <DialogContent className="max-w-md" data-testid="vendor-tag-dialog">
          <DialogHeader>
            <DialogTitle>Tag Vendors</DialogTitle>
            <p className="text-xs text-stone-500 mt-1 truncate">{vendorDialogDesign?.design_name}</p>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
              <Input
                placeholder="Search vendors..."
                value={vendorSearch}
                onChange={(e) => setVendorSearch(e.target.value)}
                className="pl-9 h-10"
                data-testid="vendor-search-input"
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setPickedVendorIds(new Set(vendors.map((v) => v.id)))}>Select All</Button>
              <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setPickedVendorIds(new Set())}>Clear</Button>
              <span className="ml-auto text-xs text-stone-500 self-center">{pickedVendorIds.size} of {vendors.length} picked</span>
            </div>
            <div className="max-h-[45vh] overflow-y-auto border border-stone-200 rounded-md divide-y divide-stone-100">
              {vendors.length === 0 ? (
                <div className="text-sm text-stone-400 text-center py-6">No vendors yet. Add vendors in Vendor Master first.</div>
              ) : (
                vendors
                  .filter((v) => !vendorSearch.trim() || v.name.toLowerCase().includes(vendorSearch.trim().toLowerCase()))
                  .map((v) => (
                    <label key={v.id} className="flex items-center gap-2 px-3 py-2 hover:bg-stone-50 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[#C2410C] cursor-pointer"
                        checked={pickedVendorIds.has(v.id)}
                        onChange={() => togglePickedVendor(v.id)}
                        data-testid={`vendor-checkbox-${v.id}`}
                      />
                      <span className="text-sm flex-1">{v.name}</span>
                    </label>
                  ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVendorDialogOpen(false)}>Cancel</Button>
            <Button
              data-testid="vendor-tag-save"
              className="bg-[#C2410C] hover:bg-[#9A3412] text-white"
              disabled={vendorBusy}
              onClick={saveVendorTags}
            >
              {vendorBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Truck className="h-4 w-4 mr-2" />}
              Save Vendors ({pickedVendorIds.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
